/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/prop-types */
import { Box, Grid, Stack, Divider } from "@mui/material";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import EventIcon from "@mui/icons-material/Event";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import BadgeIcon from "@mui/icons-material/Badge";
import StarIcon from "@mui/icons-material/Star";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TempleHinduIcon from "@mui/icons-material/TempleHindu";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import AppointMentBoolInput from "../../../../components/AppointMentBoolInput/AppointMentBoolInput";
import InputField from "../../../../components/common/new/InputField/InputField";
import Typography from "../../../../components/common/new/Typography/Typography";
import { APPOINTMENT_ENTRY } from "../../../../contants/formFields";
import { selectAllSIUsers } from "../../../../redux/selectors/user";
import { STRUCTURE_LEVEL_LIST } from "../../../../contants/appointment";
import { isEmptyObj } from "../../../../utils/helper";

export const getPosition = (position) => {
  return position?.length ? position : "NA";
};

// Info Row Component - Table style
function InfoRow({ label, value, icon: Icon }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "8px",
        background: "#FFFFFF",
        border: "1px solid #E8E8E8",
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        {Icon && (
          <Icon
            sx={{
              fontSize: 20,
              color: "#196AAB",
            }}
          />
        )}
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: "#64748B",
            letterSpacing: "0.5px",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontSize: "0.95rem",
          fontWeight: 500,
          color: "#1E293B",
          fontFamily: "'Inter', sans-serif",
          pl: 3.5,
        }}
      >
        {value || "NA"}
      </Typography>
    </Box>
  );
}

function OtherDetails({
  values,
  handleChange,
  memberDetails,
  readOnly,
  setFieldValue,
  structureLevel,
}) {
  const {
    isDistrictSi,
    isChapterSi,
    isRegionSi,
    isZoneSi,
    isAreaSi,
    isDistrictLeader,
    isChapterLeader,
    isRegionLeader,
    isZoneLeader,
    isAreaLeader,
  } = useSelector(selectAllSIUsers);

  const districtChief = isDistrictSi || isDistrictLeader;
  const chapterChief = isChapterSi || isChapterLeader;
  const regionChief = isRegionSi || isRegionLeader;
  const zoneChief = isZoneSi || isZoneLeader;
  const areaChief = isAreaSi || isAreaLeader;

  useEffect(() => {
    if (values[APPOINTMENT_ENTRY.IS_PROG_ATTENDED] === "NO") {
      setFieldValue(APPOINTMENT_ENTRY.PARTICIPATION_YEAR, "");
    }
  }, [values[APPOINTMENT_ENTRY.IS_PROG_ATTENDED]]);

  if (isEmptyObj(memberDetails)) {
    return null;
  }

  const currentPositions = memberDetails?.positions?.find(
    (position) => position?.positionType === "MAINLINE_POSITION"
  )?.name;
  const currentAppointedAt = memberDetails?.positions?.find(
    (position) => position?.positionType === "MAINLINE_POSITION"
  )?.appointedAt;

  const dualPositions = memberDetails?.positions?.find(
    (position) => position?.positionType === "DEPUTY_POSITION"
  )?.name;
  const dualAppointedAt = memberDetails?.positions?.find(
    (position) => position?.positionType === "DEPUTY_POSITION"
  )?.appointedAt;

  const specialPositions = memberDetails?.positions?.find(
    (position) => position?.positionType === "SPECIAL_GROUP_POSITION"
  )?.name;
  const specialAppointedAt = memberDetails?.positions?.find(
    (position) => position?.positionType === "SPECIAL_GROUP_POSITION"
  )?.appointedAt;

  const { gohonzonMemberships } = memberDetails;
  const { subscriptions } = memberDetails;
  const { yearsOfPractice } = memberDetails;
  const { exams } = memberDetails;

  let bsgExam = "";

  const exam40 = exams?.find((e) => e.hierarchyDepth === 40);
  const exam30 = exams?.find((e) => e.hierarchyDepth === 30);
  const exam20 = exams?.find((e) => e.hierarchyDepth === 20);
  const exam10 = exams?.find((e) => e.hierarchyDepth === 10);

  if (exam40 && exam40.examName !== null) {
    bsgExam = exam40?.examName || "NA";
  } else if (exam30 && exam30.examName !== null) {
    bsgExam = exam30?.examName || "NA";
  } else if (exam20 && exam20.examName !== null) {
    bsgExam = exam20?.examName || "NA";
  } else if (exam10 && exam10.examName !== null) {
    bsgExam = exam10?.examName || "NA";
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          background: "#FFF",
          borderRadius: "12px",
          border: "1px solid #E0E0E0",
          overflow: "hidden",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #196AAB 0%, #1976d2 100%)",
            px: 3,
            py: 2,
          }}
        >
          <Typography
            sx={{
              color: "#FFF",
              fontSize: "16px",
              fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Other Details
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: { xs: 2.5, md: 4 } }}>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {/* Left Column - Information Display */}
            <Grid item xs={12} lg={7}>
              <Box
                sx={{
                  background: "#FAFAFA",
                  borderRadius: "12px",
                  border: "1px solid #E8E8E8",
                  p: { xs: 2.5, md: 3 },
                }}
              >
                {/* Grid Layout - 2 columns */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Years of Practice"
                      value={yearsOfPractice}
                      icon={WorkHistoryIcon}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Date of Joining"
                      value={
                        memberDetails?.dateOfJoining
                          ? moment(memberDetails?.dateOfJoining).format(
                              "DD/MM/YYYY"
                            )
                          : "NA"
                      }
                      icon={EventIcon}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Current Position"
                      value={currentPositions}
                      icon={BadgeIcon}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Date of Appointment"
                      value={
                        currentAppointedAt
                          ? moment(currentAppointedAt).format("DD/MM/YYYY")
                          : "NA"
                      }
                      icon={EventIcon}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Dual Position"
                      value={dualPositions}
                      icon={StarIcon}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Date of Appointment"
                      value={
                        dualAppointedAt
                          ? moment(dualAppointedAt).format("DD/MM/YYYY")
                          : "NA"
                      }
                      icon={EventIcon}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Special Group Appointment"
                      value={specialPositions}
                      icon={GroupsIcon}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Date of Appointment"
                      value={
                        specialAppointedAt
                          ? moment(specialAppointedAt).format("DD/MM/YYYY")
                          : "NA"
                      }
                      icon={EventIcon}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="BSG Study Examination(s) Clear"
                      value={bsgExam || "NO"}
                      icon={MenuBookIcon}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Gohonzon Received"
                      value={gohonzonMemberships?.[0]?.type ? "YES" : "NO"}
                      icon={TempleHinduIcon}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Date of Receiving Gohonzon"
                      value={
                        gohonzonMemberships?.[0]?.createdAt
                          ? moment(gohonzonMemberships?.[0]?.createdAt).format(
                              "DD/MM/YYYY"
                            )
                          : "NA"
                      }
                      icon={EventIcon}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="VC Subscription"
                      value={subscriptions?.[0] ? "YES" : "NO"}
                      icon={SubscriptionsIcon}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Right Column - Input Fields */}
            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  background: "#F8FAFC",
                  borderRadius: "12px",
                  border: "1px solid #E8E8E8",
                  p: { xs: 2.5, md: 3 },
                }}
              >
                <Stack spacing={3.5}>
                  {/* Number of Shakubuku */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: "#1E293B",
                        mb: 2,
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      Number of Shakubuku
                      <Typography
                        component="span"
                        sx={{ color: "#FF6A69", ml: 0.5 }}
                      >
                        *
                      </Typography>
                    </Typography>
                    <InputField
                      type="number"
                      name={APPOINTMENT_ENTRY.NUMBER_OF_SHAKUBUL}
                      label="Number of Shakubuku"
                      value={values?.[APPOINTMENT_ENTRY.NUMBER_OF_SHAKUBUL]}
                      onChange={handleChange}
                      disabled={readOnly}
                      InputProps={{ inputProps: { min: 0 } }}
                      required
                      fullWidth
                      sx={{ mt: "5px" }}
                    />
                  </Box>

                  {/* Number of home visits */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: "#1E293B",
                        mb: 2,
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      Number of Home Visits a Week
                      <Typography
                        component="span"
                        sx={{ color: "#FF6A69", ml: 0.5 }}
                      >
                        *
                      </Typography>
                    </Typography>
                    <InputField
                      type="number"
                      name={APPOINTMENT_ENTRY.NO_OF_HOME_VISITS_WEEK}
                      label="Number of home visits a week"
                      value={values?.[APPOINTMENT_ENTRY.NO_OF_HOME_VISITS_WEEK]}
                      onChange={handleChange}
                      disabled={readOnly}
                      InputProps={{ inputProps: { min: 0 } }}
                      required
                      fullWidth
                      sx={{ mt: "5px" }}
                    />
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Training Program */}
                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="nowrap"
                    >
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "#1E293B",
                          fontFamily: "'Inter', sans-serif",
                          flexShrink: 1,
                          mr: 2,
                        }}
                      >
                        SGI Training Course/BSG Daiseido Training Programme
                        Attended
                        <Typography
                          component="span"
                          sx={{ color: "#FF6A69", ml: 0.5 }}
                        >
                          *
                        </Typography>
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        sx={{ flexShrink: 0 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            !readOnly &&
                            handleChange({
                              target: {
                                name: APPOINTMENT_ENTRY.IS_PROG_ATTENDED,
                                value: "YES",
                              },
                            })
                          }
                        >
                          <input
                            type="radio"
                            name={APPOINTMENT_ENTRY.IS_PROG_ATTENDED}
                            value="YES"
                            checked={
                              values[APPOINTMENT_ENTRY.IS_PROG_ATTENDED] ===
                              "YES"
                            }
                            onChange={handleChange}
                            disabled={readOnly}
                            style={{ marginRight: "6px" }}
                          />
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              fontFamily: "'Inter', sans-serif",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Yes
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            !readOnly &&
                            handleChange({
                              target: {
                                name: APPOINTMENT_ENTRY.IS_PROG_ATTENDED,
                                value: "NO",
                              },
                            })
                          }
                        >
                          <input
                            type="radio"
                            name={APPOINTMENT_ENTRY.IS_PROG_ATTENDED}
                            value="NO"
                            checked={
                              values[APPOINTMENT_ENTRY.IS_PROG_ATTENDED] ===
                              "NO"
                            }
                            onChange={handleChange}
                            disabled={readOnly}
                            style={{ marginRight: "6px" }}
                          />
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              fontFamily: "'Inter', sans-serif",
                              whiteSpace: "nowrap",
                            }}
                          >
                            No
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Year of participation */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: "#1E293B",
                        mb: 2,
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      If Yes, Year of Last Participation
                    </Typography>
                    <InputField
                      name={APPOINTMENT_ENTRY.PARTICIPATION_YEAR}
                      label="Year"
                      value={values[APPOINTMENT_ENTRY.PARTICIPATION_YEAR]}
                      onChange={handleChange}
                      disabled={
                        readOnly ||
                        values[APPOINTMENT_ENTRY.IS_PROG_ATTENDED] === "NO"
                      }
                      fullWidth
                      sx={{ mt: "5px" }}
                    />
                  </Box>

                  {/* Conditional: Made efforts towards fostering */}
                  {!districtChief &&
                    !STRUCTURE_LEVEL_LIST.slice(0, 1).includes(
                      structureLevel
                    ) &&
                    (chapterChief ||
                      regionChief ||
                      zoneChief ||
                      (areaChief &&
                        !STRUCTURE_LEVEL_LIST.slice(0, 4).includes(
                          structureLevel
                        ))) && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Box>
                          <AppointMentBoolInput
                            name={APPOINTMENT_ENTRY.MADE_EFFORTS_TOWARDS}
                            value={
                              values?.[APPOINTMENT_ENTRY.MADE_EFFORTS_TOWARDS]
                            }
                            handleChange={handleChange}
                            preLabel="Made Efforts Towards Fostering Capable Leaders"
                            required
                            readOnly={readOnly}
                          />
                        </Box>
                      </>
                    )}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}

export default OtherDetails;
